import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

import LoginPrompt from '../../../Auth/LoginPrompt';
import MyPromptsPanel from '../../../components/chat/MyPromptsPanel';
import { useAuth } from '../../../contexts/AuthContext';
import useReferenceMaterials from '../../../hooks/useReferenceMaterials';
import useUserPrompts from '../../../hooks/useUserPrompts';
import PersonalizationSettings, {
  MEMORY_SECTION_ID,
  PROFILE_SECTION_ID,
} from './PersonalizationSettings';
import AiReferenceSettings, { AI_REFERENCE_SECTION_ID } from './AiReferenceSettings';
import DisplaySettingsSection, { DISPLAY_SECTION_ID } from './DisplaySettingsSection';
import ReferenceMaterialsSection from './ReferenceMaterialsSection';
import SettingsSection from './SettingsSection';
import './ChatSettingsPage.css';

export const MY_PROMPTS_SECTION_ID = 'my-prompts';
export const MATERIALS_SECTION_ID = 'materials';

// 해시 딥링크 → 섹션 id. 구 /subscription#ai-memory 링크도 그대로 받는다.
export const HASH_TO_SECTION = {
  '#ai-personalization': PROFILE_SECTION_ID,
  '#ai-memory': MEMORY_SECTION_ID,
  '#my-prompts': MY_PROMPTS_SECTION_ID,
  '#materials': MATERIALS_SECTION_ID,
  '#ai-reference': AI_REFERENCE_SECTION_ID,
  '#display': DISPLAY_SECTION_ID,
};

// 내 프롬프트 섹션의 카테고리 칩 (채팅 카테고리 제목과 동일하게 유지)
export const PROMPT_CATEGORY_OPTIONS = [
  { id: 'worksheet_gen', label: '학습지' },
  { id: 'ppt_maker', label: 'PPT' },
  { id: 'image_generation', label: '이미지' },
  { id: 'record', label: '생기부' },
  { id: 'document', label: '한글(HWP)' },
  { id: 'admin_docs', label: '행정 업무' },
  { id: 'parent_communication', label: '학부모 소통' },
  { id: 'counseling_care', label: '상담·마음' },
  { id: 'pro_ai', label: '자유' },
];

const resolveInitialOpen = (hash) => {
  const target = HASH_TO_SECTION[hash];
  return target ? { [target]: true } : { [PROFILE_SECTION_ID]: true };
};

/**
 * AI 맞춤 설정 허브 (/chat/settings)
 *
 * 세로 섹션 카드 6장. 탭 없음, 첫 섹션만 기본 펼침.
 * 화면 섹션은 기기 저장이라 로그인 전에도 보인다.
 * - 전역 층: 선생님 정보·답변 스타일, AI가 기억하는 내용 (PersonalizationSettings)
 * - 작업 층: 참고 자료(자료함), 내 프롬프트, 생기부 내 스타일(링크)
 * 진입은 사이드바 하위 항목 + 채팅 안 맥락 링크(기억 토스트, + 칩 모달)에서 해시로 들어온다.
 */
const ChatSettingsPage = () => {
  const { currentUser, isAnonymous } = useAuth();
  const location = useLocation();
  const [openSections, setOpenSections] = useState(
    () => resolveInitialOpen(location.hash)
  );
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [promptCategory, setPromptCategory] = useState(
    PROMPT_CATEGORY_OPTIONS[0].id
  );

  const isLoggedIn = Boolean(currentUser) && !isAnonymous;

  const {
    prompts,
    createPrompt,
    updatePrompt,
    deletePrompt,
  } = useUserPrompts(currentUser, isAnonymous);

  const referenceMaterials = useReferenceMaterials(currentUser, isAnonymous);

  const promptsForCategory = useMemo(
    () => prompts.filter((prompt) => prompt.category === promptCategory),
    [prompts, promptCategory]
  );
  const promptCountByCategory = useMemo(() => {
    const counts = {};
    prompts.forEach((prompt) => {
      counts[prompt.category] = (counts[prompt.category] || 0) + 1;
    });
    return counts;
  }, [prompts]);

  const toggleSection = useCallback((sectionId) => {
    setOpenSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  }, []);

  // 해시가 바뀌면 해당 섹션을 열고 그 위치로 스크롤 (앱 스크롤러/문서 어느 쪽이든 동작)
  useEffect(() => {
    const target = HASH_TO_SECTION[location.hash];
    if (!target) return undefined;
    setOpenSections((prev) => (prev[target] ? prev : { ...prev, [target]: true }));
    const frame = window.requestAnimationFrame(() => {
      const element = document.getElementById(target);
      element?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.hash]);

  return (
    <>
      <Helmet>
        <title>AI 맞춤 설정 | 왓퀴즈</title>
        <meta
          name="description"
          content="선생님 정보, 답변 스타일, AI가 기억하는 내용, 참고 자료, 내 프롬프트를 한곳에서 설정해요."
        />
      </Helmet>

      <div className="csettings-page">
        <div className="csettings-container">
          <header className="csettings-header">
            <h1>AI 맞춤 설정</h1>
            <p>선생님을 알수록 답변이 정확해져요</p>
          </header>

          {!isLoggedIn ? (
            <div className="csettings-empty">
              <div className="csettings-empty-title">
                로그인하면 AI가 선생님을 기억해요
              </div>
              <div className="csettings-empty-desc">
                학교급·과목·답변 방식을 한 번만 알려주면
                <br />
                매번 설명하지 않아도 그에 맞게 답해드려요
              </div>
              <button
                className="csettings-primary-btn"
                onClick={() => setShowLoginPrompt(true)}
                type="button"
              >
                로그인하고 시작하기
              </button>
            </div>
          ) : (
            <>
              <PersonalizationSettings
                currentUser={currentUser}
                onToggleSection={toggleSection}
                openSections={openSections}
              />

              <SettingsSection
                badge={
                  referenceMaterials.materials.length > 0
                    ? referenceMaterials.materials.length
                    : undefined
                }
                description="학교 규정·평가 기준처럼 대화마다 골라 첨부하는 기준 자료"
                id={MATERIALS_SECTION_ID}
                onToggle={toggleSection}
                open={Boolean(openSections[MATERIALS_SECTION_ID])}
                title="참고 자료"
              >
                <ReferenceMaterialsSection
                  currentUser={currentUser}
                  isLoaded={referenceMaterials.isLoaded}
                  limits={referenceMaterials.limits}
                  loadError={referenceMaterials.loadError}
                  materials={referenceMaterials.materials}
                  onCreateFromFile={referenceMaterials.createFromFile}
                  onCreateText={referenceMaterials.createText}
                  onDelete={referenceMaterials.deleteMaterial}
                  onReload={referenceMaterials.reload}
                  onUpdate={referenceMaterials.updateMaterial}
                />
              </SettingsSection>

              <AiReferenceSettings
                currentUser={currentUser}
                onToggleSection={toggleSection}
                openSections={openSections}
              />

              <SettingsSection
                badge={prompts.length > 0 ? prompts.length : undefined}
                description="채팅 첫 화면 칩으로 꺼내 쓰는 자주 쓰는 요청"
                id={MY_PROMPTS_SECTION_ID}
                onToggle={toggleSection}
                open={Boolean(openSections[MY_PROMPTS_SECTION_ID])}
                title="내 프롬프트"
              >
                <div
                  aria-label="프롬프트 카테고리"
                  className="csettings-chiprow"
                  role="tablist"
                >
                  {PROMPT_CATEGORY_OPTIONS.map((option) => {
                    const count = promptCountByCategory[option.id] || 0;
                    const active = option.id === promptCategory;
                    return (
                      <button
                        aria-selected={active}
                        className={`csettings-chip ${active ? 'is-active' : ''}`}
                        key={option.id}
                        onClick={() => setPromptCategory(option.id)}
                        role="tab"
                        type="button"
                      >
                        {option.label}
                        {count > 0 && (
                          <span className="csettings-chip__count">{count}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="myprompts myprompts--inline">
                  <MyPromptsPanel
                    autoFocusBody={false}
                    createdMessage="프롬프트를 저장했어요"
                    onCreate={({ body, title }) => createPrompt({
                      body,
                      category: promptCategory,
                      title,
                    })}
                    onDelete={deletePrompt}
                    onUpdate={updatePrompt}
                    prompts={promptsForCategory}
                    resetKey={promptCategory}
                  />
                </div>
              </SettingsSection>

              <SettingsSection
                description="생기부 문체와 학교 지침은 여기서 설정해요"
                id="saenggibu-style"
                title="생기부 내 스타일"
                to="/saenggibu-my-style"
              />
            </>
          )}

          <DisplaySettingsSection
            onToggle={toggleSection}
            open={Boolean(openSections[DISPLAY_SECTION_ID])}
          />
        </div>
      </div>

      <LoginPrompt
        feature="chat"
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
      />
    </>
  );
};

export default ChatSettingsPage;
